import { useCallback } from 'react';
import { flushSync } from 'react-dom';
import { tell, getLoads } from '../api/auth';
import { useLoads } from '../context/LoadsContext';

/**
 * Encapsulates Search-page logic for conversational tools.
 */
export function useSearchTools({
  originRef,
  destinationRef,
  dateRef,
  equipmentRef,
  navigate,
  setOrigin,
  setDestination,
  setDate,
  setEquipment,
  setHighlightField,
  onResults,
}) {
  const { setLoads } = useLoads();

  // 1) Send each field to backend and fetch matching loads
  const fetchAndTellData = useCallback(async () => {
    const driverId = localStorage.getItem('driverId');
    const token = localStorage.getItem('token');
    
    if (!driverId || !token) {
      console.error('driverId or token not found in localStorage. User may not be logged in.');
      return null;
    }

    const o  = originRef.current?.value;
    const d  = destinationRef.current?.value;
    let dt  = dateRef.current?.value;
    const eq = equipmentRef.current?.value;
    if (!o || !d || !dt || !eq) return null;

    // Normalize MM/DD/YYYY → YYYY-MM-DD
    if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dt)) {
      const [m,day,y] = dt.split('/');
      dt = `${y}-${m.padStart(2,'0')}-${day.padStart(2,'0')}`;
    }

    await tell(driverId, 'origin',      o);
    await tell(driverId, 'destination', d);
    await tell(driverId, 'date',        dt);
    await tell(driverId, 'equipment',   eq);

    const { data } = await getLoads(driverId);
    return data;
  }, [originRef, destinationRef, dateRef, equipmentRef]);

  // 2) After fetching, write into context and navigate
  const performSearch = useCallback(async () => {
    // allow UI to update first
    await new Promise(r => requestAnimationFrame(r));
    localStorage.setItem('isSearching', 'true');
    const data = await fetchAndTellData();
    
    if (!data) {
      alert('Unable to perform search. Please ensure you are logged in and try again.');
      localStorage.removeItem('isSearching');
      return { success: false, data: null };
    }
    
    localStorage.setItem('loads_data', JSON.stringify(data));
    if (!data?.loads?.length) {
      alert('No loads found. From this method. Please adjust your search.');
      return { success: false, data };
    }
    
    // store in context
    setLoads(data.loads);
    
    // Navigate or call provided callback
    if (onResults) {
      onResults(data.loads);
    } else {
      console.log('Navigating to results');
      navigate('/results');
    }
    
    return { success: true, data };
  }, [fetchAndTellData, navigate, setLoads, onResults]);

  // Helper to apply highlight effect
  const highlightField = useCallback((field, action) => {
    setHighlightField(field);
    action();
    // Let the UI update - useful for ensuring highlight effect runs
    setTimeout(() => {
      setHighlightField(null);
    }, 1000); // Reset highlight after 1 second
  }, [setHighlightField]);

  // 3) Setter tools for voice
  const setOriginTool = useCallback(({ value }) => {
    if (!originRef.current) return { success: false };
    
    flushSync(() => {
      if (setOrigin) {
        setOrigin(value);
      } else {
        originRef.current.value = value;
      }
      setHighlightField('origin');
    });
    
    return { success: true, value };
  }, [originRef, setOrigin, setHighlightField]);

  const setDestinationTool = useCallback(({ value }) => {
    if (!destinationRef.current) return { success: false };
    
    flushSync(() => {
      if (setDestination) {
        setDestination(value);
      } else {
        destinationRef.current.value = value;
      }
      setHighlightField('destination');
    });
    
    return { success: true, value };
  }, [destinationRef, setDestination, setHighlightField]);

  const setDateTool = useCallback(({ value }) => {
    if (!dateRef.current) return { success: false };
    
    // normalize
    let iso = value;
    if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(value)) {
      const [m,day,y] = value.split('/');
      iso = `${y}-${m.padStart(2,'0')}-${day.padStart(2,'0')}`;
    }
    
    flushSync(() => {
      if (setDate) {
        setDate(iso);
      } else {
        dateRef.current.value = iso;
      }
      setHighlightField('date');
    });
    
    return { success: true, value: iso };
  }, [dateRef, setDate, setHighlightField]);

  const setEquipmentTool = useCallback(({ value }) => {
    if (!equipmentRef.current) return { success: false };
    
    flushSync(() => {
      if (setEquipment) {
        setEquipment(value);
      } else {
        equipmentRef.current.value = value;
      }
      setHighlightField('equipment');
    });
    
    return { success: true, value };
  }, [equipmentRef, setEquipment, setHighlightField]);

  return {
    performSearch,
    setOriginTool,
    setDestinationTool,
    setDateTool,
    setEquipmentTool,
  };
}